import React, { useContext, useMemo, useState, useEffect, lazy, Suspense } from "react";
import { Form, Button } from "antd";
import { ModalForm } from "src/enitities/Modal";
import { serializeTimestamp } from "src/enitities/Form";
import { EditOutlined } from "@ant-design/icons";
import { PricesContext } from "src/shared";
import { apiRequests } from "src/shared/api/api";
import dayjs from "dayjs";
const PriceForm = lazy(() =>
  import("src/enitities/Form").then((modal) => ({
    default: modal.PriceForm,
  }))
);

export default function EditPrice({ record, handleSave }) {
  const [isOpen, setOpen] = useState(false);
  const [changeData, setChangeData] = useState(false);
  const [fullRecord, setFullRecord] = useState(record);
  const [form] = Form.useForm();
  const { priceTypesData = [], token } = useContext(PricesContext) || {};

  const priceTypeNameToId = useMemo(() => {
    const map = new Map();
    for (const pt of priceTypesData || []) {
      if (pt?.name && pt?.id != null) map.set(pt.name, pt.id);
    }
    return map;
  }, [priceTypesData]);

  // Загружаем полные данные при открытии модального окна (как в EditNomenclature)
  useEffect(() => {
    // При закрытии модального окна сбрасываем fullRecord и форму
    if (!isOpen) {
      setFullRecord(null);
      form.resetFields();
      return;
    }
    // При открытии модального окна получаем полные данные цены
    const loadData = async () => {
      try {
        let dataToSet = null;
        if (record?.id && token) {
          const response = await apiRequests.prices.getItem(record.id, { token });
          dataToSet = response.data;
        } else if (record) {
          // Если нет ID или token, просто используем record
          dataToSet = record;
        }
        
        if (dataToSet) {
          setFullRecord(dataToSet);
          // Преобразуем даты из timestamp в dayjs объекты перед установкой в форму
          const formValues = {
            ...dataToSet,
            date_from: dataToSet.date_from ? dayjs.unix(dataToSet.date_from) : dayjs(),
            date_to: dataToSet.date_to ? dayjs.unix(dataToSet.date_to) : dayjs(),
            // Явно устанавливаем адрес и координаты
            address: dataToSet.address || undefined,
            latitude: dataToSet.latitude || undefined,
            longitude: dataToSet.longitude || undefined,
          };
          // Устанавливаем все значения в форму, включая address, latitude, longitude
          form.setFieldsValue(formValues);
        }
      } catch (error) {
        console.error("Error fetching full price data:", error);
        // Если ошибка, используем данные из record с преобразованием дат
        if (record) {
          setFullRecord(record);
          const formValues = {
            ...record,
            date_from: record.date_from ? dayjs.unix(record.date_from) : dayjs(),
            date_to: record.date_to ? dayjs.unix(record.date_to) : dayjs(),
            address: record.address || undefined,
            latitude: record.latitude || undefined,
            longitude: record.longitude || undefined,
          };
          form.setFieldsValue(formValues);
        }
      }
    };

    if (isOpen && record) {
      loadData();
    }
  }, [isOpen, record, token, form]);

  // Функция для перезапроса полных данных после сохранения
  const refreshPriceData = async () => {
    const currentRecord = fullRecord || record;
    if (currentRecord?.id && token) {
      try {
        const response = await apiRequests.prices.getItem(currentRecord.id, { token });
        const fullData = response.data;
        setFullRecord(fullData);
        // Обновляем форму с новыми данными, включая address, latitude, longitude
        const formValues = {
          ...fullData,
          date_from: fullData.date_from ? dayjs.unix(fullData.date_from) : dayjs(),
          date_to: fullData.date_to ? dayjs.unix(fullData.date_to) : dayjs(),
          address: fullData.address || undefined,
          latitude: fullData.latitude || undefined,
          longitude: fullData.longitude || undefined,
        };
        form.setFieldsValue(formValues);
        return fullData;
      } catch (error) {
        console.error("Error refreshing price data:", error);
        return null;
      }
    }
    return null;
  };

  const handleSubmit = async (data) => {
    const formData = data?.[0] || {};
    const currentRecord = fullRecord || record;

    // Нормализация price_type:
    // - если пришло строкой ("chatting") — конвертим в id
    // - если не пришло, берем из currentRecord
    const rawPriceType = formData.price_type ?? currentRecord?.price_type;
    const normalizedPriceType =
      typeof rawPriceType === "string"
        ? priceTypeNameToId.get(rawPriceType)
        : rawPriceType;

    // Собираем payload строго в API-формате (не тащим лишние поля из record)
    const payload = {
      id: currentRecord?.id,
      nomenclature: formData.nomenclature ?? currentRecord?.nomenclature ?? currentRecord?.nomenclature_id,
      price: formData.price ?? currentRecord?.price,
      price_type: normalizedPriceType,
      date_from: formData.date_from ?? currentRecord?.date_from,
      date_to: formData.date_to ?? currentRecord?.date_to,
      address: formData.address ?? currentRecord?.address,
      latitude: formData.latitude ?? currentRecord?.latitude,
      longitude: formData.longitude ?? currentRecord?.longitude,
    };

    const newData = serializeTimestamp(payload, ["date_from", "date_to"]);
    try {
      const response = await handleSave(newData);
      // После успешного сохранения загружаем полные данные из API
      // чтобы убедиться, что все поля (включая address) обновлены в таблице
      await refreshPriceData();
      return response;
    } catch (err) {
      return Promise.reject(err.response?.data.detail[0].msg);
    }
  };

  return (
    <>
      <Button icon={<EditOutlined />} onClick={() => setOpen(true)} style={{ marginRight: "5px" }} />
      <ModalForm
        width={500}
        isOpen={isOpen}
        setOpen={setOpen}
        formContext={form}
        handleSubmit={handleSubmit}
        changeData={changeData}
        setChangeData={setChangeData}>
        {isOpen ? (
          <Suspense fallback={<div></div>}>
            <PriceForm formContext={form} record={fullRecord || record} setChangeData={setChangeData} disableNomeclature={true} />
          </Suspense>
        ) : null}
      </ModalForm>
    </>
  );
}
